import React from 'react';
import { Flex, Text, List, ListItem } from '@opendoor/bricks/core';
import {
  centsToCurrencyString,
  fractionToPercentString,
} from '@opendoor/shared-fe/build/formatUtils';

import { ILateCheckoutTextData } from 'sellDirect/graphql/useLateCheckout';
import { Underwriting } from 'sellDirect/OfferContext';
import {
  FinalOfferContentItem,
  FinalOfferContentItemProps,
} from 'sellDirect/components/closingDash/finalOffer/FinalOfferContentItem';

interface IFinalOfferContent {
  rowData: FinalOfferContentItemProps[];
  renderHeader?: () => React.ReactNode;
  inModal?: boolean;
}

// component-test-check-ignore
/**
 * Final Offer Content.
 */
export const FinalOfferContent: React.FC<IFinalOfferContent> = ({
  renderHeader,
  rowData,
  ...props
}) => {
  return (
    <Flex flexDirection="column" alignItems="flex-start" data-testid="final-offer-content">
      {renderHeader && renderHeader()}
      <List variant="text" flexDir="column">
        {rowData.map((data, index) => (
          <FinalOfferContentItem
            key={data.panelId}
            isLastItem={index === rowData.length - 1}
            {...data}
            {...props}
          />
        ))}
      </List>
    </Flex>
  );
};

/** Finalized Offer Data row */
export const finalizedOfferDataRow = (underwriting: Underwriting): FinalOfferContentItemProps => ({
  title: 'Finalized offer',
  value: centsToCurrencyString(underwriting.finalizedHeadlinePriceCents),
  panelId: 'finalized-offer',
  analyticsName: 'cake-finalized-offer',
});

/** Service Charge Data row */
export const serviceChargeDataRow = (
  underwriting: Underwriting,
  shouldShowNewFee = false,
): FinalOfferContentItemProps => {
  const backupServiceChargePercentage =
    underwriting.finalizedFeeCostCents / underwriting.finalizedHeadlinePriceCents;
  const serviceChargePercentage =
    underwriting.finalizedFeePercentage || backupServiceChargePercentage || 0;
  const serviceChangePercentageText = fractionToPercentString(serviceChargePercentage);
  const currentFee = shouldShowNewFee ? '6%' : serviceChangePercentageText;
  return {
    title: 'Service charge',
    body: `Opendoor collects a ${currentFee} flat service charge that enables us to give you a stress-free sale and expert support.`,
    valueHint: currentFee,
    value: `(${centsToCurrencyString(underwriting.finalizedFeeCostCents)})`,
    panelId: 'service-charge',
    analyticsName: 'cake-service-charge',
    isAccordion: true,
  };
};

/** Estimated Closing Cost Data row */
export const estClosingCostDataRow = (underwriting: Underwriting): FinalOfferContentItemProps => {
  const closingCostsPercentage =
    underwriting.estimatedClosingCostsCents / underwriting.finalizedHeadlinePriceCents;
  return {
    title: 'Est. closing costs',
    body: 'These are costs like title insurance and appraisal fees that we don’t control. Sellers typically pay 1% to 3% of the purchase price in closing costs.',
    valueHint: fractionToPercentString(closingCostsPercentage),
    value: `(${centsToCurrencyString(underwriting.estimatedClosingCostsCents)})`,
    panelId: 'estimated-closing-costs',
    analyticsName: 'cake-estimated-closing-costs',
    isAccordion: true,
  };
};

/**
 * RepairCostBody
 */
export const RepairCostBody: React.FC = () => {
  return (
    <Flex flexDirection="column" width="100%">
      <Text fontWeight="semibold" pb={3}>
        Typical things we consider
      </Text>
      <Flex flex-direction="row" fontWeight="regular">
        <List variant="bullet" width="50%">
          <ListItem>Paint</ListItem>
          <ListItem>Roofing</ListItem>
          <ListItem>HVAC</ListItem>
        </List>
        <List variant="bullet" width="50%">
          <ListItem>Flooring</ListItem>
          <ListItem>Foundation</ListItem>
        </List>
      </Flex>
    </Flex>
  );
};

/** Repair Cost Data row */
export const repairCostDataRow = (
  shouldSeeAgentExperience: boolean,
  underwriting: Underwriting,
): FinalOfferContentItemProps => ({
  title: 'Repair costs',
  body: `Repair costs reflect the amount Opendoor will need to spend to get 
    ${shouldSeeAgentExperience ? 'your client’s' : 'your'} home market-ready. ${
    shouldSeeAgentExperience ? 'Your client' : 'You'
  } won’t need to do any repairs ${shouldSeeAgentExperience ? 'themself' : 'yourself'}.`,
  bodyExtra: <RepairCostBody />,
  value: `(${centsToCurrencyString(underwriting.repairAddendumAdjustmentCents)})`,
  panelId: 'repair-costs',
  analyticsName: 'cake-repair-costs',
  isAccordion: true,
});

/** Late Checkout Data row */
export const lateCheckoutDataRow = (
  shouldSeeAgentExperience: boolean,
  lateCheckoutTextData: ILateCheckoutTextData,
): FinalOfferContentItemProps => ({
  title: 'Late checkout',
  body: `Requesting late checkout. Cost is calculated by the amount of time 
    ${shouldSeeAgentExperience ? 'your client is' : 'you’re'} staying.`,
  value: `(${lateCheckoutTextData.totalCostDollarsText})`,
  panelId: 'late-checkout-costs',
  analyticsName: 'cake-late-checkout-costs',
  isAccordion: true,
});

/** Co-brokerage commission row */
export const coBrokeCommissionRow = (
  coBrokePercentage: number,
  underwriting: Underwriting,
): FinalOfferContentItemProps => {
  return {
    title: 'Co-op broker commission',
    body: 'Opendoor Brokerage waives any offered cooperating broker commissions.',
    value: `${centsToCurrencyString(underwriting.finalizedHeadlinePriceCents * coBrokePercentage)}`,
    panelId: 'cobroke-commission',
    analyticsName: 'cake-cobroke-commission',
    isAccordion: true,
  };
};

/** Agent referral commission row */
export const agentReferralCommissionRow = (
  isEligible: boolean,
  agentCommissionCents: number,
  agentCommissionPercentOfHeadline: number,
): FinalOfferContentItemProps => {
  return isEligible
    ? {
        title: `Your ${agentCommissionPercentOfHeadline}% commission`,
        body: 'Paid to you by Opendoor Brokerage. This is in addition to commission from your client and any Agent Access bonus.',
        value: `${centsToCurrencyString(agentCommissionCents)}`,
        valueColor: 'green50',
        panelId: 'agent-referral-commission-eligible',
        analyticsName: 'cake-agent-referral-commission-eligible',
        bold: true,
        valueHighlightIcon: 'https://imgdrop.imgix.net/da624943351f4ba2a2522e09071a504f.svg',
        isAccordion: true,
      }
    : {
        title: `Your ${agentCommissionPercentOfHeadline}% commission`,
        body: 'Please visit Opendoor.com/agent-access to review eligibility criteria for our Agent commission. If you believe this is an error, please contact your Agent Experience Partner.',
        value: 'Not eligible',
        panelId: 'agent-referral-commission-ineligible',
        analyticsName: 'cake-agent-referral-commission-ineligible',
        isAccordion: true,
      };
};

/** Net Proceeds Data row */
export const netProceedsDataRow = (
  shouldSeeAgentExperience: boolean,
  underwriting: Underwriting,
): FinalOfferContentItemProps => ({
  title: shouldSeeAgentExperience ? 'Your client’s est. net proceeds' : 'Est. net proceeds',
  value: centsToCurrencyString(underwriting.estimatedNetProceedsCents),
  panelId: 'est-net-proceeds',
  analyticsName: 'cake-est-net-proceeds',
  bold: true,
});
